/** @jsxRuntime automatic */
/** @jsxImportSource react */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Archive, Check, Eye, FlipHorizontal, History as HistoryIcon, Loader2, MousePointerClick, Pencil, Plus, Search, Sparkles, Star, Tag as TagIcon, Upload, X } from 'lucide-react';
import { ApiError, api } from '../lib/api';
import { OutfitCategoriesSection } from './OutfitCategoriesSection';
import { FullOutfitAccessSection } from './FullOutfitAccessSection';
import { CharacterReferenceSection } from './CharacterReferenceSection';
import { GENERAL_CATEGORY_ID, GENERAL_CATEGORY_NAME, categoryNameOf } from '../lib/outfitCategories';
import { PRINCIPALS_HINT, VISIBILITY_BUCKET, galleryVisibility, outfitVisibility, type OutfitBucket, type OutfitVisibility } from '../lib/outfitVisibility';
import { facingLabel, facingTone, needsFacingReview, type FacingAction } from '../lib/outfitFacing';
import {
  DAY_STORE_UNREADABLE_TEXT,
  dayAddedText,
  daySavedText,
  defaultDayInputs,
  dayOutfitAdd,
  renameBody,
  restoreBody,
  restoredText,
  updateDayBody,
  type DayDetails,
  type DayInputs,
} from '../lib/dayOutfits';
import { errorText } from '../lib/errors';
import {
  BUBBLE_SIZES,
  DEFAULT_BUBBLE_SIZE,
  MASCOT_DRAW_HINT,
  MASCOT_FEATURED_HINT,
  MASCOT_LIMITS,
  MASCOT_MIRROR,
  MASCOT_POLL_HINT,
  MASCOT_READ_ONLY_TEXT,
  MASCOT_SAVED_TEXT,
  addedText,
  alphaPlaneOf,
  analyzeAlpha,
  bytesToBase64,
  catalogCountText,
  categoryBody,
  checkMascotPng,
  defaultLook,
  defaultWindowInputs,
  duplicateOf,
  featureToggle,
  featuredStatusText,
  featuredText,
  fitInSquare,
  formatBytes,
  formatTime,
  fullText,
  galleryHistory,
  galleryOutfits,
  galleryReadiness,
  gallerySummaryLines,
  keptDuplicateOf,
  mascotAuthText,
  mascotErrorCode,
  mascotErrorReloads,
  mascotErrorText,
  mascotWarnings,
  needsPreparation,
  outfitAdd,
  outfitEdit,
  outfitThumbnailSrc,
  pacificMonthDay,
  pickCountText,
  picksFor,
  pixelRefusal,
  readinessText,
  retireConfirmText,
  retiredText,
  serviceKeepsRetired,
  sha256Hex,
  windowInputsOf,
  windowText,
  withAnnexRevision,
  ANIMATION_FILTER_LABELS,
  animationFilterAvailable,
  animationMatches,
  type AdminOutfit,
  type AlphaFindings,
  type AnimationFilter,
  type DesktopMascotCatalog,
  type MascotRect,
  type ReadinessRead,
  type WindowInputs,
} from '../lib/desktopMascot';
import { BulkOutfitsSection } from './BulkOutfitsSection';
import { DayOutfitsSection, DayRuleFields, RatingLine, type DayOutfitsWrites } from './DayOutfitsSection';
import { OutfitFacingSection } from './OutfitFacingSection';
import { OutfitPictureReplace } from './OutfitPictureReplace';
import { CelebrationsSection } from './CelebrationsSection';
import { OutfitEconomySection } from './OutfitEconomySection';
import { decodeBitmap, pixelsOf, prepareForDesktops } from './mascotCanvas';
import { WALLPAPER_LABELS, clockNow, wallpaperClass, type Wallpaper } from './mascotUi';
import { SaveNote } from './SaveNote';
import { WindowFields } from './WindowFields';
import {
  Button, Callout, Checkbox, EmptyState, Field, Icon, Input, LinkButton, OUTFIT_CHIPS, PageHeader, Panel, Segmented, Select, Skeleton, SpecChip, Stack, StatusChip, Tag,
  type Tone,
} from '../ui';
import { plural } from '../ui/format';

/** Decoded art for the preview: the bitmap, the box of pixels that catch clicks, and whether the desktop mirrors it. */
interface Art {
  bitmap: ImageBitmap;
  bounds: MascotRect | null;
  mirrored: boolean;
}

interface Staged {
  /** The file as chosen, kept so "Mirror (face left)" can prepare it again. */
  file: File;
  /** What Add sends: the chosen file, or what "Prepare for desktops" made of it. Null while it is refused. */
  base64: string | null;
  sha256: string;
  sizeBytes: number;
  width: number;
  height: number;
  refusal: string | null;
  warnings: string[];
  /** "Prepared: ..." when the file was prepared for desktops. */
  prepared: string | null;
}

type CardRead =
  | { status: 'loading' }
  | { status: 'absent' }
  | { status: 'failed'; text: string }
  | { status: 'loaded'; catalog: DesktopMascotCatalog };

/** --bad-text, mostly opaque: painted over every pixel that catches clicks on the desktop (alpha 1 or more). */
const HIT_TINT = [236, 72, 153, 200];
/** The built-in Gyro's outfit: the key of its pick count, and its tile. */
const BUILT_IN = 'default';
/** The console's own copy of the packaged Gyro (byte-identical to the agent's file). */
const BUILT_IN_SRC = '/gyro-assistant.png';
/** The chip tone of who can pick an outfit: its filter's (ok for everyone, neutral for the principals only), warn for a retired one. */
const chipTone = (visibility: OutfitVisibility): Tone => (visibility.kind === 'retired' ? 'warn' : OUTFIT_CHIPS[VISIBILITY_BUCKET[visibility.kind] ?? 'ended'].tone);
/** The tile grid's filter: every outfit, one visibility bucket, or the facing review (unclear, not checked, facing right and not mirrored). */
type StateFilter = 'all' | OutfitBucket | 'unclear';
const ANIMATION_FILTERS: readonly AnimationFilter[] = ['all', 'animated', 'still'];
const WALLPAPERS = Object.keys(WALLPAPER_LABELS) as Wallpaper[];

/**
 * Where a note shows: under the selected outfit (edit, feature, retire), under the Add panel, or in the Day outfits
 * section (under the outfit it is about, `outfitId`, while that is shown there).
 */
type Note = { ok: boolean; text: string; where: 'list' | 'add' | 'days'; outfitId?: string };
/** Whether the signed-in person may change the gallery; "unknown" acts as an admin, and the service still refuses an auditor. */
type Role = 'unknown' | 'admin' | 'auditor';
interface Editing {
  id: string;
  name: string;
  window: WindowInputs;
}

/** Decode for the preview and measure the alpha the agent trims and hit-tests by. The caller owns the bitmap. */
async function decodeArt(source: Blob, mirrored: boolean): Promise<{ art: Art; findings: AlphaFindings }> {
  const bitmap = await decodeBitmap(source);
  try {
    const findings = analyzeAlpha(alphaPlaneOf(pixelsOf(bitmap).data), bitmap.width, bitmap.height);
    return { art: { bitmap, bounds: findings.bounds, mirrored }, findings };
  } catch (error) {
    bitmap.close();
    throw error;
  }
}

/**
 * The mascot as the agent draws it: trimmed to the pixels that catch clicks, the longer side at the mascot size, drawn
 * at the screen's pixel density with high-quality smoothing, mirrored only for the packaged Gyro.
 */
function MascotPreview({ art, size, showHit }: { art: Art; size: number; showHit: boolean }) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const bounds = art.bounds;
  const fitted = bounds ? fitInSquare(size, bounds.width, bounds.height) : { width: size, height: size };
  useEffect(() => {
    const canvas = canvasRef.current;
    const context = canvas?.getContext('2d');
    if (!canvas || !context) return;
    const ratio = window.devicePixelRatio || 1;
    const width = Math.max(1, Math.round(fitted.width * ratio));
    const height = Math.max(1, Math.round(fitted.height * ratio));
    canvas.width = width;
    canvas.height = height;
    context.clearRect(0, 0, width, height);
    if (!bounds) return;
    context.imageSmoothingEnabled = true;
    context.imageSmoothingQuality = 'high';
    context.save();
    if (art.mirrored) {
      context.translate(width, 0);
      context.scale(-1, 1);
    }
    context.drawImage(art.bitmap, bounds.x, bounds.y, bounds.width, bounds.height, 0, 0, width, height);
    context.restore();
    if (showHit) {
      const image = context.getImageData(0, 0, width, height);
      for (let i = 0; i < image.data.length; i += 4) {
        if (image.data[i + 3] >= 1) image.data.set(HIT_TINT, i);
      }
      context.putImageData(image, 0, 0);
    }
  }, [art, bounds, fitted.width, fitted.height, showHit]);
  return (
    <canvas
      ref={canvasRef}
      role="img"
      aria-label={`Desktop mascot preview at size ${size}`}
      className="ui-preview-canvas"
      // The drawn size: a computed style, as the kit's meters have.
      style={{ width: fitted.width, height: fitted.height }}
    />
  );
}

const failedRead = (error: unknown): CardRead => ({
  status: 'failed',
  text: mascotAuthText(error, 'read') ?? errorText(error, 'The mascot gallery could not be read.'),
});

/**
 * One tile of the gallery grid: the thumbnail on the chosen wallpaper (the built-in Gyro mirrored with CSS, as desktops
 * show him; an outfit exactly as stored), the name and the pick count. Selecting it previews it and shows its actions.
 */
function OutfitTile({ name, src: given, version, mirrored, wallpaper, picks, featured, visibility, facing, selected, onSelect }: {
  name: string;
  src: string;
  /**
   * The image's sha256: a thumbnail loaded by its route carries it in its URL, so a picture changed in place (Replace
   * picture, a flip) is loaded again instead of the one the page already shows.
   */
  version?: string;
  mirrored: boolean;
  wallpaper: Wallpaper;
  picks: number;
  featured: boolean;
  /** Who can pick it now (lib/outfitVisibility.ts), read out with the name. */
  visibility: OutfitVisibility;
  /** Which way it faces (lib/outfitFacing.ts facingLabel): the grid's Facing column. */
  facing: string;
  selected: boolean;
  onSelect: () => void;
}) {
  const src = version && !given.startsWith('data:') ? `${given}${given.includes('?') ? '&' : '?'}v=${version.slice(0, 16)}` : given;
  return (
    <button
      type="button"
      aria-pressed={selected}
      aria-label={`${name}: ${visibility.label}${featured ? ', featured' : ''}, ${pickCountText(picks).toLowerCase()}, ${facing.toLowerCase()}`}
      title={name}
      onClick={onSelect}
      className={['ui-tile', selected ? 'is-selected' : '', visibility.kind === 'ended' ? 'is-ended' : ''].filter(Boolean).join(' ')}
    >
      <span className={`ui-tile-pic ${wallpaperClass(wallpaper)}`}>
        <img src={src} loading="lazy" decoding="async" alt="" className={mirrored ? 'ui-tile-img is-mirrored' : 'ui-tile-img'} />
        {featured && <Star size={13} aria-hidden="true" fill="currentColor" className="ui-tile-star" />}
      </span>
      <span className="ui-tile-name">{name}</span>
      <span className="ui-tile-picks">{picks.toLocaleString('en-US')} picked</span>
      <span className="ui-tile-picks ui-tile-facing">{facing}</span>
    </button>
  );
}

/**
 * Outfits and mascot › Gallery (GET /v1/admin/desktop/mascot and its outfit routes): the outfits IT admins upload for
 * the floating Gyro, which of them are in the gallery people pick from in Gyro's settings and when, and the featured
 * outfits: people who have not picked an outfit get the first featured outfit in the gallery now. Featuring is a toggle
 * on each outfit that leaves the others featured (PATCH with `featured`). Day outfits (owner, 2026-10-06), swapped in
 * for everyone on their day with their greeting, are added here too ("Day outfit") and listed in their own section
 * (DayOutfitsSection.tsx) with the retired outfits: Retire keeps every outfit, never deletes it, and Restore brings it
 * back under the same id. Add several outfits (BulkOutfitsSection.tsx; owner, 2026-10-09) adds a whole folder of outfits
 * into one category, one after another. Every change is in the audit trail. An auditor can read it, with every change
 * button disabled; the service refuses an auditor's change anyway, and the page says so. On a service with no desktop
 * app the page says so and holds nothing else. Rebuilt on the kit (overhaul step 4): the page is its own now, under
 * Outfits and mascot, with the preview beside the tiles, the selected outfit's inspector under them, and each section a
 * panel of its own; the state is the one container it always was.
 */
export function DesktopMascotCard() {
  const [read, setRead] = useState<CardRead>({ status: 'loading' });
  const [readiness, setReadiness] = useState<ReadinessRead>({ status: 'loading' });
  const [role, setRole] = useState<Role>('unknown');
  const [defaultArt, setDefaultArt] = useState<Art | null>(null);
  const [outfitArt, setOutfitArt] = useState<{ id: string; art: Art | null } | null>(null);
  const [stagedArt, setStagedArt] = useState<Art | null>(null);
  const [staged, setStaged] = useState<Staged | null>(null);
  const [preparing, setPreparing] = useState(false);
  const [mirror, setMirror] = useState(false);
  const [name, setName] = useState('');
  const [addWindow, setAddWindow] = useState<WindowInputs>(() => defaultWindowInputs(Date.now()));
  const [feature, setFeature] = useState(false);
  // The category a new outfit goes in (its id; GENERAL_CATEGORY_ID for none). Kept after an add: a season of outfits
  // is usually added one after another.
  const [addCategory, setAddCategory] = useState<string>(GENERAL_CATEGORY_ID);
  // What Add adds: an outfit with a gallery window, or a day outfit with a day rule and a greeting.
  const [addKind, setAddKind] = useState<'window' | 'day'>('window');
  const [addDay, setAddDay] = useState<DayInputs>(() => defaultDayInputs(Date.now()));
  const [editing, setEditing] = useState<Editing | null>(null);
  // The selected tile ("default" or an outfit id); null follows what people get by default.
  const [selected, setSelected] = useState<string | null>(null);
  // The preview shows the chosen file instead of the selected tile.
  const [previewStaged, setPreviewStaged] = useState(false);
  const [now, setNow] = useState(() => Date.now());
  const [size, setSize] = useState<number>(DEFAULT_BUBBLE_SIZE);
  const [wallpaper, setWallpaper] = useState<Wallpaper>('checker');
  const [showHit, setShowHit] = useState(false);
  // The tile grid's search, state filter and animation filter (layout only: the catalog is read whole, as before).
  const [query, setQuery] = useState('');
  const [stateFilter, setStateFilter] = useState<StateFilter>('all');
  const [animationFilter, setAnimationFilter] = useState<AnimationFilter>('all');
  // 'bulk': Add several outfits is uploading (BulkOutfitsSection.tsx); every other change waits for it.
  const [busy, setBusy] = useState<'add' | 'edit' | 'feature' | 'retire' | 'restore' | 'day' | 'picture' | 'category' | 'bulk' | 'facing' | null>(null);
  const [note, setNote] = useState<Note | null>(null);
  const stageToken = useRef(0);
  const fileInput = useRef<HTMLInputElement>(null);
  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  // Each decoded image is closed when it is replaced and when the card goes away (no image memory left behind).
  useEffect(() => () => defaultArt?.bitmap.close(), [defaultArt]);
  useEffect(() => () => outfitArt?.art?.bitmap.close(), [outfitArt]);
  useEffect(() => () => stagedArt?.bitmap.close(), [stagedArt]);

  // States follow the clock: an outfit joins or leaves the gallery while the page is open.
  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 60_000);
    return () => window.clearInterval(timer);
  }, []);

  const applyView = useCallback((catalog: DesktopMascotCatalog) => {
    setRead({ status: 'loaded', catalog });
    setNow(Date.now());
    // A selection or an edit of an outfit that is gone (another admin retired it), or not in the gallery grid (a day
    // outfit, which its own section lists), falls back to the default.
    const gallery = galleryOutfits(catalog);
    const known = (id: string) => gallery.some((outfit) => outfit.id === id);
    setSelected((current) => (current !== null && current !== BUILT_IN && !known(current) ? null : current));
    setEditing((current) => (current && !known(current.id) ? null : current));
    // A category chosen for the next add that another admin removed meanwhile: back to General.
    setAddCategory((current) => (current === GENERAL_CATEGORY_ID || catalog.categories?.categories.some((category) => category.id === current) ? current : GENERAL_CATEGORY_ID));
  }, []);

  useEffect(() => {
    let live = true;
    api.desktopMascot().then(
      (catalog) => {
        if (!live) return;
        if (catalog === null) setRead({ status: 'absent' });
        else applyView(catalog);
      },
      (error: unknown) => {
        if (live) setRead(failedRead(error));
      },
    );
    api.me().then(
      (result) => {
        if (live) setRole(result.user?.isAdmin ? 'admin' : result.user?.isAuditor ? 'auditor' : 'unknown');
      },
      () => {
        // Unknown: the buttons stay on, and the service refuses an auditor's change with a 403 the card words.
      },
    );
    api.supportDevices().then(
      (result) => {
        if (live) setReadiness({ status: 'loaded', ...galleryReadiness(result.devices) });
      },
      (error: unknown) => {
        if (live) setReadiness({ status: 'failed', httpStatus: error instanceof ApiError ? error.status : undefined });
      },
    );
    return () => {
      live = false;
    };
  }, [applyView]);

  // The built-in Gyro, from the console's own copy (byte-identical to the agent's packaged file), mirrored as desktops show him.
  useEffect(() => {
    let live = true;
    void (async () => {
      try {
        const response = await fetch(BUILT_IN_SRC);
        if (!response.ok) return;
        const { art } = await decodeArt(await response.blob(), MASCOT_MIRROR.packaged);
        if (live) setDefaultArt(art);
        else art.bitmap.close();
      } catch {
        // No default preview; nothing else depends on it.
      }
    })();
    return () => {
      live = false;
    };
  }, []);

  const catalog = read.status === 'loaded' ? read.catalog : null;
  // The outfits of the gallery grid: never a day outfit (its own section lists it) or a retired outfit's leftover copy.
  const gallery = useMemo(() => (catalog ? galleryOutfits(catalog) : []), [catalog]);
  const featuredNow = catalog ? defaultLook(catalog, now) : null;
  const selectedId = selected ?? featuredNow?.id ?? BUILT_IN;
  const selectedOutfit = selectedId !== BUILT_IN ? gallery.find((outfit) => outfit.id === selectedId) ?? null : null;
  const selectedOutfitId = selectedOutfit?.id ?? null;
  // Its image's hash too, so the preview loads the new picture after Replace picture (or a flip) changes it in place.
  const selectedOutfitSha = selectedOutfit?.sha256 ?? null;
  // Who people who have not picked an outfit see now and next, and the selected outfit's featured line: each walks the
  // coming year, so they are worked out again only when the catalog, the clock or the selection changes.
  const summaryLines = useMemo(() => (catalog ? gallerySummaryLines(catalog, now, { timeZone }) : []), [catalog, now, timeZone]);
  // Who can pick each outfit now (lib/outfitVisibility.ts): the groups, the everyone group first even when it holds
  // only the built-in Gyro, the filter counts and the summary line; judged again as the clock moves. The sets as the
  // service answered them: absent on a service before sets, null while it cannot read them (the summary says so).
  const today = pacificMonthDay(now);
  const visibility = useMemo(() => galleryVisibility(gallery, catalog?.categories, today, now), [gallery, catalog, today, now]);
  const selectedStatus = useMemo(
    () => (selectedOutfit ? featuredStatusText(selectedOutfit, gallery, now, { timeZone }) : null),
    [gallery, selectedOutfit, now, timeZone],
  );

  // The selected outfit's full image, as desktops receive it (after the service removed any metadata).
  useEffect(() => {
    setOutfitArt(null);
    if (!selectedOutfitId) return;
    let live = true;
    void (async () => {
      try {
        const blob = await api.desktopMascotOutfitImage(selectedOutfitId);
        const decoded = blob ? await decodeArt(blob, MASCOT_MIRROR.custom) : null;
        if (!live) {
          decoded?.art.bitmap.close();
          return;
        }
        setOutfitArt({ id: selectedOutfitId, art: decoded?.art ?? null });
      } catch {
        if (live) setOutfitArt({ id: selectedOutfitId, art: null });
      }
    })();
    return () => {
      live = false;
    };
  }, [selectedOutfitId, selectedOutfitSha]);

  /** Read the catalog again; answers it, or null when it could not be read (the card then shows why). */
  const load = async (): Promise<DesktopMascotCatalog | null> => {
    try {
      const fresh = await api.desktopMascot();
      if (fresh === null) {
        setRead({ status: 'absent' });
        return null;
      }
      applyView(fresh);
      return fresh;
    } catch (error) {
      setRead(failedRead(error));
      return null;
    }
  };

  /**
   * After a refused or half-done write: read the catalog again when the refusal means it changed, then say why (after
   * `prefix`, what was saved before the refusal), under the outfit it is about in the Day outfits section (`outfitId`),
   * in the words of what was tried (`action`: a full catalog asks to retire one before an add or a restore).
   */
  const writeFailed = async (error: unknown, fallback: string, where: Note['where'], options: { prefix?: string; outfitId?: string; action?: 'add' | 'restore' } = {}) => {
    const fresh = mascotErrorReloads(error) ? await load() : null;
    const known = fresh ?? catalog;
    const named = known ? [...known.outfits, ...(known.retired ?? []), ...(known.orphans ?? [])] : [];
    setNote({ ok: false, where, outfitId: options.outfitId, text: `${options.prefix ?? ''}${mascotErrorText(error, named, fallback, options.action)}` });
  };

  /** Check a chosen file, prepare it for desktops when it needs it, and stage the result for the preview and Add. */
  const stage = async (file: File, mirrorIt: boolean) => {
    const token = (stageToken.current += 1);
    setPreparing(true);
    setNote(null);
    let art: Art | null = null;
    try {
      const original = new Uint8Array(await file.arrayBuffer());
      let check = checkMascotPng(original);
      let bytes = original;
      let source: Blob = file;
      let prepared: string | null = null;
      let refusal: string | null = null;
      if (needsPreparation(check, mirrorIt)) {
        try {
          const result = await prepareForDesktops(file, mirrorIt);
          if (result.ok) {
            source = result.blob;
            bytes = new Uint8Array(await result.blob.arrayBuffer());
            check = checkMascotPng(bytes);
            prepared = result.line;
          } else {
            refusal = result.message;
          }
        } catch {
          refusal = 'This browser could not prepare the image for desktops. Export it within the limits below and choose it again.';
        }
      }
      refusal ??= check.refusal?.message ?? null;
      let findings: AlphaFindings | null = null;
      if (check.header) {
        try {
          const decoded = await decodeArt(source, MASCOT_MIRROR.custom);
          art = decoded.art;
          findings = decoded.findings;
          refusal ??= pixelRefusal(decoded.findings)?.message ?? null;
        } catch {
          refusal ??= 'This browser could not decode the PNG. Export the image again.';
        }
      }
      const sha256 = await sha256Hex(bytes);
      if (token !== stageToken.current) return; // another file was chosen meanwhile
      setStaged({
        file,
        base64: refusal === null ? bytesToBase64(bytes) : null,
        sha256,
        sizeBytes: bytes.length,
        width: check.header?.width ?? 0,
        height: check.header?.height ?? 0,
        refusal,
        warnings: mascotWarnings(check.header, findings),
        prepared,
      });
      setStagedArt(art);
      setPreviewStaged(true);
      art = null; // the staged art now owns the bitmap
    } catch (error) {
      if (token === stageToken.current) setNote({ ok: false, where: 'add', text: errorText(error, 'The file could not be read.') });
    } finally {
      art?.bitmap.close();
      if (token === stageToken.current) setPreparing(false);
    }
  };

  const discard = () => {
    stageToken.current += 1;
    setPreparing(false);
    setStaged(null);
    setStagedArt(null);
    setPreviewStaged(false);
  };

  const resetAddForm = () => {
    discard();
    setName('');
    setFeature(false);
    setAddWindow(defaultWindowInputs(clockNow()));
    setAddDay(defaultDayInputs(clockNow()));
  };

  const select = (id: string) => {
    setSelected(id);
    setPreviewStaged(false);
    setEditing((current) => (current && current.id !== id ? null : current));
  };

  const add = async () => {
    if (!catalog || !staged?.base64) return;
    const at = clockNow();
    setNow(at);
    // An outfit with a gallery window, or a day outfit: its day rule and greeting, with the annex revision too.
    const ready = addKind === 'day' && catalog.annexRevision !== undefined
      ? dayOutfitAdd({ pngBase64: staged.base64, name, day: addDay }, catalog.revision, catalog.annexRevision ?? null)
      : outfitAdd({ pngBase64: staged.base64, name, window: addWindow, featured: feature, category: addCategory }, catalog.revision, at);
    if (ready.kind === 'refused') {
      setNote({ ok: false, where: 'add', text: ready.message });
      return;
    }
    setBusy('add');
    setNote(null);
    try {
      const body = ready.body;
      if ('day' in body) {
        // A day outfit is not in the gallery grid: its own section lists it, and the note says when it comes.
        const added = await api.addDesktopMascotDayOutfit(body);
        applyView(added);
        resetAddForm();
        setNote({ ok: true, where: 'add', text: dayAddedText(added, at) });
        return;
      }
      const added = await api.addDesktopMascotOutfit(body);
      applyView(added);
      resetAddForm();
      setSelected(added.outfitId);
      setEditing(null);
      setNote({ ok: true, where: 'add', text: addedText(added, at, { timeZone }) });
    } catch (error) {
      // Saved with its audit record queued: the form is done. Any other refusal keeps the file and the form.
      if (mascotErrorCode(error) === 'mascot_audit_pending') resetAddForm();
      await writeFailed(error, 'The outfit was not added.', 'add', { action: 'add' });
    } finally {
      setBusy(null);
    }
  };

  const saveEdit = async () => {
    if (!catalog || !editing) return;
    const outfit = catalog.outfits.find((item) => item.id === editing.id);
    if (!outfit) {
      setEditing(null);
      return;
    }
    const at = clockNow();
    setNow(at);
    const change = outfitEdit(outfit, editing, catalog.revision, at);
    if (change.kind === 'unchanged') {
      setEditing(null);
      return;
    }
    if (change.kind === 'refused') {
      setNote({ ok: false, where: 'list', text: change.message });
      return;
    }
    // Pinned to this outfit before the write, as the Featured toggle does (see there): a new window can change who people
    // get by default, which the panel follows while no tile is selected.
    setSelected(outfit.id);
    setBusy('edit');
    setNote(null);
    try {
      applyView(await api.updateDesktopMascotOutfit(outfit.id, withAnnexRevision(change.body, outfit, catalog.annexRevision)));
      setEditing(null);
      setNote({ ok: true, where: 'list', text: MASCOT_SAVED_TEXT });
    } catch (error) {
      if (mascotErrorCode(error) === 'mascot_audit_pending') setEditing(null);
      await writeFailed(error, 'The outfit was not saved.', 'list');
    } finally {
      setBusy(null);
    }
  };

  /** The Category select on an outfit: it moves to that category (or to General), and nothing else about it changes. */
  const setCategory = async (outfit: AdminOutfit, category: string | null) => {
    if (!catalog) return;
    // Pinned to this outfit before the write, as the Featured toggle is (see there).
    setSelected(outfit.id);
    setBusy('category');
    setNote(null);
    try {
      const fresh = await api.updateDesktopMascotOutfit(outfit.id, withAnnexRevision(categoryBody(category, catalog.revision), outfit, catalog.annexRevision));
      applyView(fresh);
      setNote({ ok: true, where: 'list', text: `“${outfit.name}” is now in ${categoryNameOf(category, fresh.categories?.categories)}.` });
    } catch (error) {
      await writeFailed(error, 'The outfit was not moved.', 'list');
    } finally {
      setBusy(null);
    }
  };

  /** The Featured toggle: this outfit joins or leaves the featured outfits; every other featured outfit stays featured. */
  const toggleFeatured = async (outfit: AdminOutfit) => {
    if (!catalog) return;
    // Pinned to this outfit before the write. While no tile is selected the panel follows the outfit people get by
    // default, which this toggle changes: the answer, or the catalog read again after a refusal, would otherwise move
    // the panel, its note and its focused Featured toggle to another featured outfit. applyView still lets go of it if
    // another admin retired the outfit meanwhile.
    setSelected(outfit.id);
    setBusy('feature');
    setNote(null);
    try {
      const fresh = await api.updateDesktopMascotOutfit(outfit.id, withAnnexRevision(featureToggle(outfit, catalog.revision), outfit, catalog.annexRevision));
      applyView(fresh);
      const changed = fresh.outfits.find((item) => item.id === outfit.id);
      if (changed) setNote({ ok: true, where: 'list', text: featuredText(changed) });
    } catch (error) {
      await writeFailed(error, outfit.featured ? 'The outfit is still featured.' : 'The outfit was not featured.', 'list');
    } finally {
      setBusy(null);
    }
  };

  /**
   * Retire an outfit once the admin confirms `question`: DELETE with the catalog revision, and the annex revision when the
   * service sent one. It leaves the gallery and the featured outfits but is kept, never deleted (Restore brings it back),
   * unless the service is a gyro-svc from before Retire, whose DELETE removes it (its question and note say so).
   */
  const retire = async (outfit: Pick<AdminOutfit, 'id' | 'name'>, question: string, where: Note['where']) => {
    if (!catalog || !window.confirm(question)) return;
    const keeps = serviceKeepsRetired(catalog);
    setBusy('retire');
    setNote(null);
    try {
      applyView(await api.retireDesktopMascotOutfit(outfit.id, catalog.revision, catalog.annexRevision ?? undefined));
      setNote({ ok: true, where, outfitId: outfit.id, text: retiredText(outfit.name, keeps) });
    } catch (error) {
      await writeFailed(error, 'The outfit was not retired.', where, { outfitId: outfit.id });
    } finally {
      setBusy(null);
    }
  };

  /**
   * An admin's own decision on which way an outfit faces (lib/outfitFacing.ts): flip it so it faces left, undo the
   * flip, mark it as fine as it is, or ask the model again; then the catalog is read again (a flip changes the image).
   */
  const setFacing = async (outfit: Pick<AdminOutfit, 'id' | 'name'>, action: FacingAction) => {
    setBusy('facing');
    setNote(null);
    try {
      const answer = await api.setDesktopMascotOutfitFacing(outfit.id, action);
      await load();
      setNote({ ok: true, where: 'list', outfitId: outfit.id, text: `“${outfit.name}”: ${facingLabel(answer.facing).toLowerCase()}.` });
    } catch (error) {
      await writeFailed(error, 'The facing was not changed.', 'list', { outfitId: outfit.id });
    } finally {
      setBusy(null);
    }
  };

  /** Restore a retired outfit, or one that never reached the gallery, under its own id (both revisions). */
  const restore = async (outfit: Pick<AdminOutfit, 'id' | 'name'>) => {
    const annexRevision = catalog?.annexRevision;
    if (!catalog || typeof annexRevision !== 'number') return;
    const wasFeatured = (catalog.retired ?? []).find((kept) => kept.id === outfit.id)?.wasFeatured === true;
    setBusy('restore');
    setNote(null);
    try {
      const fresh = await api.restoreDesktopMascotOutfit(outfit.id, restoreBody(catalog.revision, annexRevision));
      applyView(fresh);
      setNote({ ok: true, where: 'days', outfitId: outfit.id, text: restoredText(fresh, outfit.id, clockNow(), undefined, wasFeatured) });
    } catch (error) {
      await writeFailed(error, 'The outfit was not restored.', 'days', { outfitId: outfit.id, action: 'restore' });
    } finally {
      setBusy(null);
    }
  };

  /**
   * Edit's Save on a day outfit: its new name with PATCH (the name alone, never a window), then its day with PUT …/day.
   * The PUT goes with the catalog revision the rename answered, but always with the annex revision the card held when Save
   * was pressed: a rename never changes the annex, so taking the rename's would let a form loaded before another admin's
   * day change overwrite it. Answers whether the change was saved, so the form can close (saved with its audit record
   * queued or missing counts, as the other forms treat it). A rename saved with its record queued (503
   * mascot_audit_pending) stops there: the day change is not sent, the form stays open, and the note says so.
   */
  const saveDay = async (outfit: Pick<AdminOutfit, 'id' | 'name'>, change: { name?: string; details?: DayDetails }): Promise<boolean> => {
    const annexRevision = catalog?.annexRevision;
    if (!catalog || typeof annexRevision !== 'number') return false;
    // A day outfit kept beyond the catalog (past its first 100) is renamed in the annex: the rename goes with the annex
    // revision held, and moves it on by one, so the day change then goes with the one the rename left (any other change
    // meanwhile still refuses it).
    const stored = catalog.outfits.find((item) => item.id === outfit.id);
    let dayAnnexRevision = annexRevision;
    setBusy('day');
    setNote(null);
    let view: DesktopMascotCatalog = catalog;
    let step: 'rename' | 'day' = change.name !== undefined ? 'rename' : 'day';
    try {
      if (change.name !== undefined) {
        view = await api.updateDesktopMascotOutfit(outfit.id, withAnnexRevision(renameBody(change.name, view.revision), stored, annexRevision));
        applyView(view);
        if (stored?.placement === 'overflow' && view.annexRevision === annexRevision + 1) dayAnnexRevision = view.annexRevision;
        step = 'day';
      }
      if (change.details) {
        view = await api.updateDesktopMascotDay(outfit.id, updateDayBody(change.details, view.revision, dayAnnexRevision));
        applyView(view);
      }
      setNote({ ok: true, where: 'days', outfitId: outfit.id, text: daySavedText(change.name ?? outfit.name) });
      return true;
    } catch (error) {
      const code = mascotErrorCode(error);
      if (step === 'rename' && (code === 'mascot_audit_pending' || code === 'mascot_audit_failed') && change.details) {
        // The new name is saved, its record queued (or, for an outfit kept beyond the catalog, refused and listed in
        // History); the day change was never sent. The form stays open to send it.
        await load();
        setNote({
          ok: false,
          where: 'days',
          outfitId: outfit.id,
          text: code === 'mascot_audit_pending'
            ? 'The new name is saved, but its audit record is queued because the audit ledger refused it just now, so the day change was not sent. Save again to send it.'
            : 'The new name is saved, but the audit ledger refused its record just now (History lists the change), so the day change was not sent. Save again to send it.',
        });
        return false;
      }
      await writeFailed(error, 'The day outfit was not saved.', 'days', { outfitId: outfit.id, prefix: step === 'day' && change.name !== undefined ? 'The new name is saved. ' : '' });
      return code === 'mascot_audit_pending' || code === 'mascot_audit_failed';
    } finally {
      setBusy(null);
    }
  };

  /** What the Day outfits section asks the card to do (its writes go through the card's lock, notes and reloads). */
  const dayWrites: DayOutfitsWrites = {
    save: saveDay,
    retire: (outfit, question) => retire(outfit, question, 'days'),
    restore,
    refuse: (outfitId, text) => setNote({ ok: false, where: 'days', outfitId, text }),
  };

  if (read.status === 'absent') {
    return (
      <>
        <PageHeader />
        <div className="ui-page">
          <EmptyState icon={Sparkles} title="This service has no desktop app">The outfit gallery appears here on a service that runs the desktop mascot.</EmptyState>
        </div>
      </>
    );
  }

  const readOnly = role === 'auditor';
  const writeLocked = readOnly || busy !== null || catalog === null;
  // Why nothing can be added now (the 500 kept in all, retired ones included), from the service's limits.
  const fullNote = catalog ? fullText(catalog) : null;
  const full = fullNote !== null;
  // Day outfits need a service that has them (an older one sends no annex revision) and can read them now (not null).
  const daysOffered = catalog !== null && catalog.annexRevision !== undefined;
  const daysReadable = typeof catalog?.annexRevision === 'number';
  // A service that has day outfits but cannot read them now (annexRevision null) cannot tell them from the gallery's own
  // outfits, and refuses (503) every add, retire, feature and window change until it can: those are paused here too, and
  // the grid says why. Renaming still works.
  const annexUnreadable = catalog !== null && catalog.annexRevision === null;
  // Outfit categories need a service that has them (an older one sends none) and can read them now (not null).
  const categoriesOffered = catalog !== null && catalog.categories !== undefined;
  const categories = catalog?.categories ?? null;
  const categoryOptions = categories ? categories.categories : [];
  const addingDay = addKind === 'day' && daysOffered;
  const addLocked = readOnly || full || busy !== null || preparing || annexUnreadable;
  const canAdd = catalog !== null && !addLocked && Boolean(staged?.base64) && name.trim().length > 0 && (!addingDay || daysReadable);
  const duplicate = staged && catalog ? duplicateOf(staged.sha256, catalog.outfits) : null;
  const keptDuplicate = staged && catalog && !duplicate ? keptDuplicateOf(staged.sha256, catalog) : null;
  const editUnchanged = Boolean(catalog && editing && selectedOutfit && editing.id === selectedOutfit.id
    && outfitEdit(selectedOutfit, editing, catalog.revision, now).kind === 'unchanged');

  const builtInVisibility = outfitVisibility('built-in', categories, today, now);
  // History: the catalog's lines and the service's other history (day changes, retires, restores) as one list.
  const history = catalog ? galleryHistory(catalog, formatTime) : [];
  // The tiles shown: the search narrows by name, the filter by who can pick them (the built-in Gyro: everyone, always).
  const needle = query.trim().toLowerCase();
  const nameMatches = (candidate: string) => needle === '' || candidate.toLowerCase().includes(needle);
  // The facing review (lib/outfitFacing.ts): outfits judged unclear, never judged, or facing right and not mirrored.
  const reviewCount = gallery.filter(needsFacingReview).length;
  // The Animation filter (owner, 2026-10-19), offered while the service marks which outfits have one; its counts take
  // in the built-in Gyro, which follows its own binding, as the state filter's "Everyone" count does.
  const animationOffered = catalog ? animationFilterAvailable(catalog) : false;
  const animationCounts = { animated: gallery.filter((outfit) => outfit.hasActions === true).length + (catalog?.builtInHasActions === true ? 1 : 0) };
  const shownGroups = visibility.groups
    .filter((group) => stateFilter === 'all' || stateFilter === 'unclear' || group.bucket === stateFilter)
    .map((group) => ({ ...group, outfits: group.outfits.filter((outfit) => nameMatches(outfit.name) && (stateFilter !== 'unclear' || needsFacingReview(outfit)) && animationMatches(animationFilter, outfit.hasActions)) }));
  const builtInShown = (stateFilter === 'all' || stateFilter === 'everyone') && nameMatches('Built-in Gyro') && animationMatches(animationFilter, catalog?.builtInHasActions);
  const shownCount = shownGroups.reduce((sum, group) => sum + group.outfits.length, 0) + (builtInShown ? 1 : 0);

  // The preview: the chosen file when it is shown, else the selected tile.
  const previewKind: 'staged' | 'outfit' | 'built-in' = previewStaged && staged ? 'staged' : selectedOutfit ? 'outfit' : 'built-in';
  const outfitArtNow = outfitArt && selectedOutfit && outfitArt.id === selectedOutfit.id ? outfitArt : null;
  const shown = previewKind === 'staged' ? stagedArt : previewKind === 'outfit' ? outfitArtNow?.art ?? null : defaultArt;
  // Why there is no preview, when there will not be one (otherwise it is still loading).
  const noPreview = shown ? null
    : previewKind === 'staged' ? (preparing ? null : 'No preview: this file cannot be read as a PNG.')
      : previewKind === 'outfit' && outfitArtNow && !outfitArtNow.art ? 'The stored image could not be loaded for the preview.' : null;
  const shownSize = shown?.bounds ? fitInSquare(size, shown.bounds.width, shown.bounds.height) : null;
  const selectedVisibility = selectedOutfit ? visibility.of(selectedOutfit) : null;
  const caption = previewKind === 'staged'
    ? staged?.refusal ? 'Your chosen image. It cannot be added as it is.' : 'Your chosen image, as desktops will show it (not added yet).'
    : selectedOutfit
      ? `“${selectedOutfit.name}”, as desktops show it.${selectedVisibility?.audience === 'principals' ? ' Only the principals can pick it now.' : ''}`
      : 'The built-in Gyro, mirrored as desktops show him.';

  const intro = (
    <span>
      The floating Gyro on each desktop. People pick their outfit under Gyro&apos;s outfits in Gyro&apos;s settings, from this gallery: the built-in Gyro
      is always there, next to the outfits added here. Keep some outfits in the gallery for good and give others a window,
      such as one month, or the same months every year (say every October). {MASCOT_FEATURED_HINT} Day outfits come on
      their own day for everyone, with their greeting. Retiring an outfit keeps it, never deletes it. {PRINCIPALS_HINT} Only the floating
      mascot changes; the chat avatar does not.
    </span>
  );

  return (
    <>
      <PageHeader
        meta={catalog ? (
          <>
            <StatusChip tone="neutral" plain>{catalogCountText(catalog, gallery.length)}</StatusChip>
            <span aria-live="polite">{readinessText(readiness)}</span>
          </>
        ) : read.status === 'loading' ? <Skeleton width="220px" /> : null}
      />
      <div className="ui-page">
        <Stack>
          {read.status === 'failed' ? (
            <Callout tone="bad" role="alert" title="The outfit gallery could not be read">{read.text}</Callout>
          ) : !catalog ? (
            <div className="ui-gallery" aria-busy="true">
              <Panel title="Outfits" description={intro}>
                <div className="ui-tiles" aria-hidden="true">{Array.from({ length: 8 }, (_, i) => <Skeleton key={i} size="xl" className="ui-tile-sk" />)}</div>
                <span className="ui-sr">Loading the mascot gallery</span>
              </Panel>
              <Panel title="Preview"><Skeleton size="xl" className="ui-preview-sk" /></Panel>
            </div>
          ) : (
            <>
              {readOnly && <Callout tone="warn">{MASCOT_READ_ONLY_TEXT}</Callout>}
              {annexUnreadable && (
                <Callout tone="warn" role="alert" title="Day outfits and retired outfits could not be read just now">
                  Gyro cannot tell day outfits from the gallery&apos;s own: every outfit in the catalog is shown here, and the counts include the day outfits.
                  Adding, retiring, featuring and window changes are paused until it can; renaming still works. Reload in a minute.
                </Callout>
              )}

              <div className="ui-gallery">
                <Panel
                  flush
                  className="ui-gallery-main"
                  title={<>Outfits ({catalogCountText(catalog, gallery.length)})</>}
                  description={[visibility.summary, ...summaryLines].join(' ')}
                  footer={<span>{MASCOT_POLL_HINT} {intro}</span>}
                >
                  <div className="ui-panel-pad ui-panel-pad--divided">
                    <div className="ui-toolbar">
                      <Input size="sm" prefix={<Icon icon={Search} size="sm" />} placeholder="Search outfits" aria-label="Search outfits" value={query} onChange={(e) => setQuery(e.target.value)} className="ui-w-md" />
                      <Segmented<StateFilter>
                        label="Show"
                        value={stateFilter}
                        onChange={setStateFilter}
                        options={[
                          { value: 'all', label: `All ${gallery.length + 1}` },
                          { value: 'everyone', label: `${OUTFIT_CHIPS.everyone.label} ${visibility.counts.everyone + 1}` },
                          { value: 'principals', label: `${OUTFIT_CHIPS.principals.label} ${visibility.counts.principals}` },
                          { value: 'scheduled', label: `${OUTFIT_CHIPS.scheduled.label} ${visibility.counts.scheduled}` },
                          { value: 'ended', label: `${OUTFIT_CHIPS.ended.label} ${visibility.counts.ended}` },
                          { value: 'unclear', label: `Facing to review ${reviewCount}` },
                        ]}
                      />
                      {animationOffered && <Segmented<AnimationFilter>
                        label="Animation"
                        value={animationFilter}
                        onChange={setAnimationFilter}
                        options={ANIMATION_FILTERS.map((filter) => ({
                          value: filter,
                          label: `${ANIMATION_FILTER_LABELS[filter]} ${filter === 'all' ? gallery.length + 1 : filter === 'animated' ? animationCounts.animated : gallery.length + 1 - animationCounts.animated}`,
                        }))}
                      />}
                    </div>
                  </div>
                  <div className="ui-panel-pad ui-tile-groups">
                    {shownCount === 0 && <EmptyState compact icon={Search} title="No outfit matches">Clear the search or pick another state.</EmptyState>}
                    {shownGroups.map((group) => {
                      const showsBuiltIn = group.key === 'everyone' && builtInShown;
                      if (group.outfits.length === 0 && !showsBuiltIn) return null;
                      const contents = showsBuiltIn
                        ? `the built-in Gyro${group.outfits.length > 0 ? ` and ${plural(group.outfits.length, 'outfit')}` : ''}`
                        : plural(group.outfits.length, 'outfit');
                      // A scheduled or ended group shares one date window, worded by this browser's clock.
                      const span = group.window ? windowText(group.window, { timeZone }, now) : null;
                      return (
                        <div key={group.key} className="ui-tile-group">
                          <div className="ui-tile-group-head">
                            <SpecChip spec={OUTFIT_CHIPS[group.bucket]} />
                            <span className="ui-tile-group-title">{group.title}</span>
                            <span className="ui-cell-sub">· {contents}{span && <> · {span}</>}</span>
                          </div>
                          <div role="group" aria-label={group.title} className="ui-tiles">
                            {showsBuiltIn && (
                              <OutfitTile
                                name="Built-in Gyro"
                                src={BUILT_IN_SRC}
                                mirrored={MASCOT_MIRROR.packaged}
                                wallpaper={wallpaper}
                                picks={picksFor(catalog.picks, BUILT_IN)}
                                featured={false}
                                visibility={builtInVisibility}
                                facing="Faces left"
                                selected={selectedId === BUILT_IN}
                                onSelect={() => select(BUILT_IN)}
                              />
                            )}
                            {group.outfits.map((outfit) => (
                              <OutfitTile
                                key={outfit.id}
                                name={outfit.name}
                                src={outfitThumbnailSrc(outfit)}
                                mirrored={MASCOT_MIRROR.custom}
                                version={outfit.sha256}
                                wallpaper={wallpaper}
                                picks={picksFor(catalog.picks, outfit.id)}
                                featured={outfit.featured}
                                visibility={visibility.of(outfit)}
                                facing={facingLabel(outfit.facing)}
                                selected={selectedId === outfit.id}
                                onSelect={() => select(outfit.id)}
                              />
                            ))}
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {/* The selected outfit: its facts and actions. */}
                  <div className="ui-panel-pad ui-panel-pad--top ui-inspector" aria-live="polite">
                    {selectedOutfit && selectedVisibility ? (
                      <div className="ui-form">
                        <div className="ui-cell-title">
                          <strong className="ui-inspector-name">{selectedOutfit.name}</strong>
                          <StatusChip tone={chipTone(selectedVisibility)}>{selectedVisibility.label}</StatusChip>
                          {selectedOutfit.featured && <StatusChip tone="warn" icon={Star}>Featured</StatusChip>}
                          {featuredNow?.id === selectedOutfit.id && <StatusChip tone="info">Default now</StatusChip>}
                        </div>
                        <div className="ui-cell-stack">
                          <span>{selectedVisibility.why}</span>
                          <span className="ui-cell-sub">Date window: {windowText(selectedOutfit.window, { timeZone }, now)}.</span>
                          {selectedStatus && <span>{selectedStatus}</span>}
                          <span className="ui-cell-sub">
                            {pickCountText(picksFor(catalog.picks, selectedOutfit.id))} · {selectedOutfit.width} x {selectedOutfit.height} px · {formatBytes(selectedOutfit.sizeBytes)}
                            {' '}· added by {selectedOutfit.addedBy} on {formatTime(selectedOutfit.addedAtMs)}
                            {selectedOutfit.updatedAtMs !== selectedOutfit.addedAtMs && <> · last changed by {selectedOutfit.updatedBy} on {formatTime(selectedOutfit.updatedAtMs)}</>}
                          </span>
                          <RatingLine rating={selectedOutfit.rating} />
                          <span className="ui-cell-sub" data-testid="facing-line">
                            Facing: <StatusChip tone={facingTone(selectedOutfit.facing)}>{facingLabel(selectedOutfit.facing)}</StatusChip>
                            {selectedOutfit.facing?.facingSource === 'admin' && <> · decided by an admin</>}
                          </span>
                        </div>
                        {categoriesOffered && (
                          <div className="ui-actions">
                            <Field label="Category" help={categories === null ? 'Categories cannot be read just now.' : undefined}>
                              <Select
                                size="sm"
                                aria-label={`Category of ${selectedOutfit.name}`}
                                className="ui-w-md"
                                value={selectedOutfit.category ?? GENERAL_CATEGORY_ID}
                                disabled={writeLocked || categories === null}
                                onChange={(e) => void setCategory(selectedOutfit, e.target.value === GENERAL_CATEGORY_ID ? null : e.target.value)}
                              >
                                <option value={GENERAL_CATEGORY_ID}>{GENERAL_CATEGORY_NAME}</option>
                                {categoryOptions.map((category) => <option key={category.id} value={category.id}>{category.name}</option>)}
                              </Select>
                            </Field>
                            {busy === 'category' && <Icon icon={Loader2} size="sm" className="ui-spin" label="Saving the category" />}
                            <LinkButton variant="ghost" size="sm" icon={TagIcon} href="#mascot-categories-title" title="The sets, below: switch one on or off, or set its season">Categories</LinkButton>
                          </div>
                        )}
                        <div className="ui-actions">
                          <Button
                            size="sm"
                            icon={Pencil}
                            disabled={writeLocked}
                            aria-expanded={editing?.id === selectedOutfit.id}
                            onClick={() => setEditing(editing?.id === selectedOutfit.id ? null : { id: selectedOutfit.id, name: selectedOutfit.name, window: windowInputsOf(selectedOutfit.window, now) })}
                          >
                            Edit
                          </Button>
                          {/* A toggle on this outfit alone: featuring it never stops featuring another. */}
                          <Button
                            size="sm"
                            icon={busy === 'feature' ? Loader2 : Star}
                            aria-pressed={selectedOutfit.featured}
                            title={MASCOT_FEATURED_HINT}
                            disabled={writeLocked || annexUnreadable}
                            onClick={() => void toggleFeatured(selectedOutfit)}
                          >
                            Featured
                          </Button>
                          <Button
                            variant="danger"
                            size="sm"
                            icon={Archive}
                            busy={busy === 'retire'}
                            disabled={writeLocked || annexUnreadable}
                            onClick={() => void retire(selectedOutfit, retireConfirmText(selectedOutfit, catalog, clockNow(), { timeZone, keeps: serviceKeepsRetired(catalog) }), 'list')}
                          >
                            Retire
                          </Button>
                        </div>
                        {/* Replace picture (OutfitPictureReplace.tsx): a new PNG for this outfit, in place; everything else about it stays. */}
                        <OutfitPictureReplace
                          key={selectedOutfit.id}
                          outfit={selectedOutfit}
                          revision={catalog.revision}
                          annexRevision={catalog.annexRevision}
                          known={[...catalog.outfits, ...(catalog.retired ?? []), ...(catalog.orphans ?? [])]}
                          disabled={writeLocked || annexUnreadable}
                          wallpaper={wallpaper}
                          onBusy={(on) => setBusy(on ? 'picture' : null)}
                          reload={load}
                        />
                        {/* Which way it faces (lib/outfitFacing.ts): flip the stored image so it faces left, undo that, or say it is fine as it is. */}
                        <div className="ui-actions" data-testid="facing-actions">
                          {selectedOutfit.facing?.mirrored ? (
                            <Button size="sm" icon={FlipHorizontal} busy={busy === 'facing'} disabled={writeLocked} title="Put the image back as it was uploaded" onClick={() => void setFacing(selectedOutfit, 'undo')}>
                              Undo flip
                            </Button>
                          ) : (
                            <Button size="sm" icon={FlipHorizontal} busy={busy === 'facing'} disabled={writeLocked} title="Mirror the image so it faces left; the upload is kept, so this can be undone" onClick={() => void setFacing(selectedOutfit, 'flip')}>
                              Flip
                            </Button>
                          )}
                          {!selectedOutfit.facing?.mirrored && selectedOutfit.facing?.facing !== 'left' && (
                            <Button size="sm" variant="ghost" disabled={writeLocked} title="It faces left as it is: take it off the review list" onClick={() => void setFacing(selectedOutfit, 'mark-left')}>
                              Faces left as it is
                            </Button>
                          )}
                          {!selectedOutfit.facing?.mirrored && selectedOutfit.facing?.facing !== 'front' && (
                            <Button size="sm" variant="ghost" disabled={writeLocked} title="It faces the viewer: take it off the review list" onClick={() => void setFacing(selectedOutfit, 'mark-front')}>
                              Faces front
                            </Button>
                          )}
                          {!selectedOutfit.facing?.mirrored && (
                            <Button size="sm" variant="ghost" disabled={writeLocked} title="Ask the model again which way it faces (mirrors it if it faces right)" onClick={() => void setFacing(selectedOutfit, 'judge')}>
                              Judge again
                            </Button>
                          )}
                        </div>
                        {editing && editing.id === selectedOutfit.id && (
                          <div className="ui-row-editor">
                            <div className="ui-form">
                              <Field label="Name">
                                <Input
                                  id="mascot-edit-name"
                                  maxLength={MASCOT_LIMITS.nameMax}
                                  value={editing.name}
                                  disabled={busy !== null}
                                  onChange={(e) => {
                                    const value = e.target.value;
                                    setEditing((current) => current && { ...current, name: value });
                                  }}
                                />
                              </Field>
                              <WindowFields
                                idPrefix="mascot-edit"
                                value={editing.window}
                                now={now}
                                timeZone={timeZone}
                                disabled={busy !== null || annexUnreadable}
                                onChange={(value) => setEditing((current) => current && { ...current, window: value })}
                              />
                              <div className="ui-actions">
                                <Button variant="primary" size="sm" icon={Check} busy={busy === 'edit'} disabled={busy !== null || editUnchanged} onClick={() => void saveEdit()}>Save</Button>
                                <Button variant="ghost" size="sm" icon={X} disabled={busy !== null} onClick={() => setEditing(null)}>Cancel</Button>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="ui-form">
                        <div className="ui-cell-title">
                          <strong className="ui-inspector-name">Built-in Gyro</strong>
                          <StatusChip tone={chipTone(builtInVisibility)}>{builtInVisibility.label}</StatusChip>
                          {!featuredNow && <StatusChip tone="info">Default now</StatusChip>}
                        </div>
                        <div className="ui-cell-stack">
                          <span className="ui-cell-sub">Everyone, always: in every gallery, and cannot be retired. Mirrored to face left, as desktops show him.</span>
                          <span className="ui-cell-sub">{pickCountText(picksFor(catalog.picks, BUILT_IN))}</span>
                        </div>
                      </div>
                    )}
                    <SaveNote note={note?.where === 'list' ? note : null} />
                  </div>
                </Panel>

                <Panel title="Preview" description="As the desktop draws it" className="ui-gallery-aside">
                  <div className="ui-form">
                    <div className={`ui-preview-box ${wallpaperClass(wallpaper)}`}>
                      {shown ? (
                        <MascotPreview art={shown} size={size} showHit={showHit} />
                      ) : noPreview ? (
                        <span className="ui-preview-none">{noPreview}</span>
                      ) : (
                        <Icon icon={Loader2} size="lg" className="ui-spin" label="Loading the preview" />
                      )}
                    </div>
                    <p className="ui-cell-sub">
                      {caption}
                      {shownSize && <> At mascot size {size}: {shownSize.width} x {shownSize.height}.</>}
                    </p>
                    <Field label="Mascot size">
                      <Segmented<string>
                        label="Mascot size"
                        value={String(size)}
                        onChange={(value) => setSize(Number(value))}
                        options={BUBBLE_SIZES.map((option) => ({ value: String(option), label: String(option) }))}
                      />
                    </Field>
                    <Field label="Wallpaper">
                      <Segmented<Wallpaper>
                        label="Wallpaper"
                        value={wallpaper}
                        onChange={setWallpaper}
                        options={WALLPAPERS.map((option) => ({ value: option, label: WALLPAPER_LABELS[option] }))}
                      />
                    </Field>
                    <Checkbox checked={showHit} onChange={(e) => setShowHit(e.target.checked)} label={<span className="ui-title-icon"><Icon icon={MousePointerClick} size="sm" />Clickable area</span>} />
                  </div>
                </Panel>
              </div>

              <Panel
                title={<span id="mascot-add-title" className="ui-title-icon"><Icon icon={Plus} size="md" />Add an outfit</span>}
                description="A PNG with a transparent background, named, with its gallery window or its day"
              >
                <div className="ui-form">
                  {fullNote && <Callout tone="warn">{fullNote}</Callout>}
                  <fieldset disabled={readOnly || full || annexUnreadable} className="ui-fieldset">
                    <div className="ui-form">
                      {daysOffered && (
                        <Field
                          label="What to add"
                          help={addKind === 'day'
                            ? 'Swapped in for everyone on its day, with its greeting; never featured.'
                            : 'With a date window: always, between two dates, or in chosen months every year. Who can pick it also depends on its set (Categories, below).'}
                        >
                          <Segmented<'window' | 'day'>
                            label="What to add"
                            size="md"
                            value={addKind}
                            onChange={setAddKind}
                            options={[{ value: 'window', label: 'Gallery outfit' }, { value: 'day', label: 'Day outfit' }]}
                          />
                        </Field>
                      )}
                      <Field
                        label="Image"
                        help={`${MASCOT_DRAW_HINT} A PNG with a transparent background, 8-bit RGBA, at most ${MASCOT_LIMITS.maxSide.toLocaleString('en-US')} pixels a side and ${MASCOT_LIMITS.maxBytes.toLocaleString('en-US')} bytes. A larger file is prepared for desktops here first.`}
                      >
                        <div className="ui-actions">
                          <Button icon={Upload} disabled={addLocked} onClick={() => fileInput.current?.click()}>Choose PNG…</Button>
                          <input
                            ref={fileInput}
                            type="file"
                            accept="image/png"
                            hidden
                            disabled={addLocked}
                            onChange={(e) => {
                              const file = e.target.files?.[0];
                              e.target.value = ''; // the same file can be chosen again
                              if (file) void stage(file, mirror);
                            }}
                          />
                          {preparing && <Icon icon={Loader2} size="md" className="ui-spin" label="Checking and preparing the image" />}
                        </div>
                      </Field>
                      <Checkbox
                        checked={mirror}
                        disabled={busy !== null || preparing}
                        onChange={(e) => {
                          const next = e.target.checked;
                          setMirror(next);
                          if (staged) void stage(staged.file, next);
                        }}
                        label={<><strong>Mirror (face left)</strong> <span className="ui-quiet">flips the chosen image before it is prepared and added</span></>}
                      />

                      {staged && (
                        <div className="ui-staged">
                          <div className="ui-actions">
                            <strong>{staged.file.name}</strong>
                            <span className="ui-cell-sub">{staged.width} x {staged.height} px · {staged.sizeBytes.toLocaleString('en-US')} bytes</span>
                            <Button size="sm" icon={Eye} aria-pressed={previewKind === 'staged'} onClick={() => setPreviewStaged(true)}>Preview</Button>
                            <Button variant="ghost" size="sm" icon={X} disabled={busy !== null} onClick={discard}>Discard</Button>
                          </div>
                          {staged.prepared && <p className="ui-cell-sub">{staged.prepared}</p>}
                          {staged.refusal ? (
                            <Callout tone="bad" role="alert">{staged.refusal}</Callout>
                          ) : (
                            <p className="ui-note is-ok"><span>Meets every desktop rule; the service checks it again when you add it.</span></p>
                          )}
                          {duplicate && (
                            <Callout tone="warn">This exact image is already in the catalog as “{duplicate.name}”; the service refuses a second copy.</Callout>
                          )}
                          {keptDuplicate && (
                            <Callout tone="warn">
                              {keptDuplicate.retired
                                ? `This exact image is the retired outfit “${keptDuplicate.outfit.name}”: restore it under Retired, in Day outfits below, instead of adding it again.`
                                : `This exact image is “${keptDuplicate.outfit.name}”, which never reached the gallery: restore it under Needs attention, in Day outfits below.`}
                            </Callout>
                          )}
                          {staged.warnings.length > 0 && (
                            <Callout tone="warn">
                              <ul className="ui-list">{staged.warnings.map((warning) => <li key={warning}>{warning}</li>)}</ul>
                            </Callout>
                          )}
                          <p className="ui-cell-sub">SHA-256 <Tag>{staged.sha256}</Tag></p>
                        </div>
                      )}

                      <Field label="Name" help={`People see this name in their gallery. ${name.trim().length} / ${MASCOT_LIMITS.nameMax} characters.`}>
                        <Input
                          id="mascot-add-name"
                          required
                          maxLength={MASCOT_LIMITS.nameMax}
                          value={name}
                          disabled={busy !== null}
                          onChange={(e) => setName(e.target.value)}
                          placeholder="Halloween 2026"
                        />
                      </Field>
                      {addingDay ? (
                        daysReadable
                          ? <DayRuleFields idPrefix="mascot-add-day" value={addDay} now={now} disabled={busy !== null} onChange={setAddDay} />
                          : <Callout tone="warn" role="alert">{DAY_STORE_UNREADABLE_TEXT}</Callout>
                      ) : (
                        <>
                          <WindowFields idPrefix="mascot-add" value={addWindow} now={now} timeZone={timeZone} disabled={busy !== null} onChange={setAddWindow} />
                          <Checkbox
                            checked={feature}
                            disabled={busy !== null}
                            onChange={(e) => setFeature(e.target.checked)}
                            label={<><strong>Feature it</strong> <span className="ui-quiet">(people who have not picked an outfit get the first featured outfit that is in the gallery now; the outfits already featured stay featured)</span></>}
                          />
                          {categoriesOffered && (
                            <Field label="Category" help="The section people see it under. Add categories below.">
                              <Select
                                id="mascot-add-category"
                                className="ui-w-md"
                                value={addCategory}
                                disabled={busy !== null || categories === null}
                                onChange={(e) => setAddCategory(e.target.value)}
                              >
                                <option value={GENERAL_CATEGORY_ID}>{GENERAL_CATEGORY_NAME}</option>
                                {categoryOptions.map((category) => <option key={category.id} value={category.id}>{category.name}</option>)}
                              </Select>
                            </Field>
                          )}
                        </>
                      )}
                      <div className="ui-actions">
                        <Button variant="primary" icon={Check} busy={busy === 'add'} disabled={!canAdd} onClick={() => void add()}>
                          {addingDay ? 'Add the day outfit' : 'Add to the gallery'}
                        </Button>
                        {!staged && !readOnly && !full && <span className="ui-cell-sub">Choose a PNG and give it a name to add it.</span>}
                      </div>
                    </div>
                  </fieldset>
                  <SaveNote note={note?.where === 'add' ? note : null} />
                </div>
              </Panel>

              {/* Many outfits into one category at once; while it uploads, the card's other changes wait (busy 'bulk'). */}
              <BulkOutfitsSection
                catalog={catalog}
                now={now}
                timeZone={timeZone}
                wallpaper={wallpaper}
                disabled={readOnly || full || annexUnreadable}
                waiting={busy !== null && busy !== 'bulk'}
                onCatalog={applyView}
                onBusy={(on) => setBusy(on ? 'bulk' : null)}
                reload={load}
              />

              {categoriesOffered && (
                <OutfitCategoriesSection
                  view={categories}
                  locked={readOnly || busy !== null}
                  onSaved={async () => {
                    // Every outfit row names its category: read the catalog again so the names and counts are current.
                    await load();
                  }}
                />
              )}

              {/* Which way outfits face: the counts, the review list and the backfill (hidden on a service without it). */}
              <OutfitFacingSection locked={readOnly || busy !== null} onChanged={load} onSelect={select} />

              {/* The outfit economy (owner, 2026-10-15): rarity, copies, collections and packs (hidden while the service has it off). */}
              <OutfitEconomySection catalog={catalog} locked={readOnly || busy !== null} />

              {/* Recognition celebrations (owner, 2026-10-21): the clips a recognized person's Gyro plays, each with its switch (hidden while the service has the feature off). */}
              <CelebrationsSection locked={readOnly || busy !== null} />

              {/* Who always sees every outfit: its own setting, read by the section (hidden on a service without it). */}
              <FullOutfitAccessSection locked={readOnly || busy !== null} />

              {/* The character reference (owner, 2026-10-31): the identity reference Gyro supplies with every render ask, replaceable here (hidden on a service without it). */}
              <CharacterReferenceSection locked={readOnly || busy !== null} />

              {daysOffered && (
                <DayOutfitsSection
                  catalog={catalog}
                  now={now}
                  wallpaper={wallpaper}
                  locked={readOnly || busy !== null}
                  note={note?.where === 'days' ? note : null}
                  writes={dayWrites}
                />
              )}

              {history.length > 0 && (
                <Panel flush title={<span className="ui-title-icon"><Icon icon={HistoryIcon} size="md" />History ({history.length})</span>} description="Every change to the gallery, newest first">
                  <details className="ui-details ui-details--panel">
                    <summary>Show the history</summary>
                    <ul className="ui-list ui-history">
                      {history.map((line) => <li key={line.key}>{line.text}</li>)}
                    </ul>
                  </details>
                </Panel>
              )}
            </>
          )}
        </Stack>
      </div>
    </>
  );
}
